import { N as Notyf } from './notyf-DIw0RvBj.js';
import { P as Pubsub } from './pubsub-DKqIEqaC.js';

class ProductVariantSelect extends HTMLElement {
  abortController;

  connectedCallback() {
    this.initOptions();
  }

  /**
   * Initialize the options.
   */
  initOptions() {
    this.options = this.querySelectorAll('[data-variant-id]');
    this.url = this.dataset.url;
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.qsProductId;
    this.sectionToFetch = this.dataset.sectionToFetch || ',aon';

    this.addEventListener('change', (event) => {
      const target = event.target.closest('[data-variant-id]');
      if (target && !this.abortController) this.onVariantChange({ target });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target.closest('[data-variant-id]');
      if (target && !this.abortController) {
        event.preventDefault();
        target.checked = true;
        this.onVariantChange({ target });
      }
    });
  }

  /**
   * Handle variant change.
   * @param {Event} event - The event object.
   */
  async onVariantChange({ target }) {
    if (this.abortController) return;

    Pubsub.publish(`variant:change:${this.productId}`);

    this.abortController = new AbortController();

    const { variantId } = target.dataset;

    const url = this.createFetchUrl(variantId);

    // Immediately uncheck all other checkboxes within the same fieldset to improve perceived speed.
    this.updateFieldset(target);

    try {
      const response = await fetch(url, {
        signal: this.abortController.signal,
      });

      const text = await response.text();
      const parsedHTML = new DOMParser().parseFromString(text, 'text/html');

      Pubsub.publish(`variant:changed:${this.productId}`, {
        variantId,
        html: parsedHTML,
      });

      this.initOptions();

      this.updateURL(variantId);
    } catch (error) {
      console.error("Failed to change variant", error);
      Notyf.error(window.toast_translations.product_variant_select.variant_error);
      if (error.name === 'AbortError')
        return console.error("AbortError", error);

    } finally {
      this.abortController = null;
      document.querySelector(`#${target.id}`)?.focus();
    }
  }

  /**
   * Update URL
   * @param {String} value - URL value
   */
  updateURL(value) {
    if (!window.location.pathname.includes('/products/')) return;

    const { searchParams } = new URL(window.location);
    searchParams.set('variant', value);

    // Replace the current history state with the new search params, without reloading the page
    history.replaceState(null, '', `${window.location.pathname}?${searchParams}`);
  }

  createFetchUrl(variantId) {
    const url = new URL(this.url, window.location.origin);
    url.searchParams.set('section_id', this.sectionToFetch);
    url.searchParams.set('variant', variantId);

    return url.toString();
  }

  /**
   * Update the fieldset.
   * @param {HTMLElement} target - The target element.
   */
  updateFieldset(target) {
    const fieldset = target.closest('fieldset');
    if (!fieldset) return;

    fieldset.querySelectorAll('[data-variant-id]').forEach((option) => {
      option.checked = option === target;
    });
  }
}

customElements.define('product-variant-select', ProductVariantSelect);
